import qs from 'qs'
import { CompactEncrypt, compactDecrypt, importJWK, base64url } from 'jose'
const encoder = new TextEncoder()
const decoder = new TextDecoder('utf-8', { fatal: true })
const policies = new Map()
const random = n => crypto.getRandomValues(new Uint8Array(n))
export async function secureFetch(input, init = {}, options = {}) {
  const nativeFetch = options.fetch || window.fetch.bind(window)
  const url = new URL(input, window.location.origin)
  if (url.origin !== window.location.origin || !/^\/(api|admin-api)\//.test(url.pathname)) {
    throw new Error('加密接口必须同源')
  }
  const scope = url.pathname.startsWith('/admin-api/') ? 'admin' : 'web'
  const prefix = scope === 'admin' ? '/admin-api' : '/api'
  let policy = policies.get(prefix)
  if (!policy || policy.until < Date.now()) {
    const response = await nativeFetch(prefix + '/secure/keys', { credentials: 'same-origin', cache: 'no-store', signal: init.signal })
    if (!response.ok) throw new Error('无法获取接口加密配置')
    policy = { ...await response.json(), until: Date.now() + 60000 }
    if (policy.v !== 1 || !['off', 'optional', 'required'].includes(policy.mode)) throw new Error('接口加密配置无效')
    policies.set(prefix, policy)
  }
  const query = { ...qs.parse(url.search.slice(1), { depth: 8, allowPrototypes: false }), ...(options.query || {}) }
  if (policy.mode === 'off') {
    if (options.required) throw new Error('服务端未启用接口加密')
    if (options.query) url.search = qs.stringify(query)
    return nativeFetch(url.href, init)
  }
  const method = (init.method || 'GET').toUpperCase()
  const body = init.body == null ? null : JSON.parse(init.body)
  const rid = base64url.encode(random(16))
  const replyKey = random(32)
  try {
    const jwk = (policy.keys || []).find(key => key.kid === policy.activeKid)
    if (!jwk) throw new Error('接口加密公钥不可用')
    const key = await importJWK(jwk, 'RSA-OAEP-256')
    const payload = { v: 1, scope, rid, iat: Math.floor(Date.now() / 1000), method,
      path: url.pathname, query, body, replyKey: base64url.encode(replyKey) }
    const token = await new CompactEncrypt(encoder.encode(JSON.stringify(payload)))
      .setProtectedHeader({ alg: 'RSA-OAEP-256', enc: 'A256GCM', kid: policy.activeKid,
        typ: 'spark-request+jwe', cty: 'application/json' }).encrypt(key)
    const headers = new Headers(init.headers || {})
    headers.set('Content-Type', 'application/jose'); headers.set('Accept', 'application/jose')
    const response = await nativeFetch(prefix + '/secure/dispatch', { ...init, method: 'POST', headers,
      credentials: 'same-origin', cache: 'no-store', body: token })
    if (!(response.headers.get('content-type') || '').startsWith('application/jose')) {
      const failure = await response.json().catch(() => null)
      if (failure && failure.code === 'PAYLOAD_KEY_UNKNOWN' && !options.retried) {
        policies.delete(prefix)
        return secureFetch(input, init, { ...options, retried: true })
      }
      throw new Error(failure?.message || '接口加密响应无效')
    }
    const { plaintext, protectedHeader } = await compactDecrypt(await response.text(), replyKey,
      { keyManagementAlgorithms: ['dir'], contentEncryptionAlgorithms: ['A256GCM'] })
    if (protectedHeader.typ !== 'spark-response+jwe' || protectedHeader.cty !== 'application/json') throw new Error('接口响应类型无效')
    const result = JSON.parse(decoder.decode(plaintext))
    if (result.v !== 1 || result.scope !== scope || result.rid !== rid || result.status !== response.status) throw new Error('接口响应不匹配')
    const outputHeaders = new Headers(response.headers)
    outputHeaders.delete('content-length'); outputHeaders.delete('content-encoding')
    outputHeaders.set('content-type', result.contentType)
    const output = result.contentType.includes('application/json') ? JSON.stringify(result.body)
      : Uint8Array.from(atob(result.body.data), ch => ch.charCodeAt(0))
    return new Response(output, { status: response.status, headers: outputHeaders })
  } finally { replyKey.fill(0) }
}
